'use client';
/**
 * AUTORISATION PONCTUELLE D'UN RESPONSABLE — 06/09/2026, demande d'Inès.
 *
 * ── Le geste qu'on outille ───────────────────────────────────────────────
 *
 * Un technicien seul à la paillasse tombe sur un mur qu'un « responsable »
 * (biologiste, RQ…) peut seul lever — une dérogation CQI/CQE, une annulation
 * de passage. Jusqu'ici, lever ce mur exigeait que le responsable se déplace
 * et ouvre SA session sur le poste, ou que le technicien lui donne son
 * propre mot de passe. Ni l'un ni l'autre n'est praticable en routine.
 *
 * ── Ce que ce composant fait, et ne fait PAS ────────────────────────────
 *
 * Il recueille un identifiant + mot de passe à fusionner dans LE MÊME appel
 * que le geste à autoriser (`responsable: payload`). Il n'appelle aucune
 * route de connexion : c'est le serveur, dans `autoriserResponsable`
 * (`backend/src/api/ged/controllers/autorisation.ts`), qui vérifie ces
 * identifiants exactement comme un login (même table, même comparaison), SANS
 * émettre de jeton ni poser de cookie. La session du technicien qui a le
 * clavier n'est ni lue ni modifiée.
 *
 * ── Où l'afficher ────────────────────────────────────────────────────────
 *
 * UNIQUEMENT quand la session en cours n'a pas déjà le droit demandé — qui
 * l'a peut agir directement, sans ce formulaire (`if (peutDirectement) { ... }
 * else { <ChampsResponsable .../> }`, jamais les deux en même temps).
 */
import React, { useState } from 'react';
import { I, inputStyle } from './ui';

export type ResponsablePayload = { identifiant: string; motDePasse: string } | undefined;

export function useAutorisationResponsable() {
  const [identifiant, setIdentifiant] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const pret = identifiant.trim().length > 0 && motDePasse.length > 0;
  const reinitialiser = () => { setIdentifiant(''); setMotDePasse(''); };
  const payload: ResponsablePayload = pret ? { identifiant: identifiant.trim(), motDePasse } : undefined;
  return { identifiant, setIdentifiant, motDePasse, setMotDePasse, pret, payload, reinitialiser };
}

export function ChampsResponsable({ identifiant, setIdentifiant, motDePasse, setMotDePasse, libelle }: {
  identifiant: string; setIdentifiant: (v: string) => void;
  motDePasse: string; setMotDePasse: (v: string) => void;
  /** Ce que le responsable autorise — apparaît dans le petit texte
   *  d'explication, ex. « cette dérogation », « cette annulation ». */
  libelle: string;
}) {
  return (
    <div style={{ display: 'grid', gap: 6, padding: '8px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--amber-50, #FFF8EC)', border: '1px solid var(--amber-300, #EFC77B)' }}>
      <strong style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--amber-700, #8a5a06)', display: 'flex', alignItems: 'center', gap: 5 }}>
        <I n="lock" s={12} />Autorisation d’un responsable
      </strong>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input value={identifiant} onChange={(e) => setIdentifiant(e.target.value)}
          placeholder="Identifiant du responsable" autoComplete="off"
          style={{ ...inputStyle, fontSize: 12.5, width: 190 }} />
        <input type="password" value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)}
          placeholder="Mot de passe" autoComplete="off"
          style={{ ...inputStyle, fontSize: 12.5, width: 150 }} />
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        Un responsable saisit ici son identifiant pour autoriser {libelle} — ceci n’ouvre pas de
        session, ni vous ni lui n’êtes déconnectés.
      </span>
    </div>
  );
}
